import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useNavigate, useLocation } from "react-router-dom";
import { Image, Trash, Loader2, ImageOff } from "lucide-react";
import { MonoValue } from "@/components/redesign/MonoValue";
import { StatusBadge } from "@/components/redesign/StatusBadge";
import ImageViewDialog from "@/components/ImageViewDialog";
import SecureImage from "@/components/SecureImage";
import {
  SwipeableList,
  SwipeableListItem,
  TrailingActions,
  SwipeAction,
} from 'react-swipeable-list';
import 'react-swipeable-list/dist/styles.css';
import DeleteConfirmationDialog from "@/components/DeleteConfirmationDialog";
import { toast } from "sonner";
import { entriesAPI } from "@/api/entries";
import { peopleAPI } from "@/api/people";
import { useAuth } from "@/contexts/AuthContext";
import useSubscription from "@/contexts/SubscriptionContext";
import { canUploadImages } from "@/lib/featureGates";
import { supabase } from "@/integrations/supabase/client";
import { useProjectRole } from "@/hooks/useProjectRole";

interface Entry {
  id: string;
  type: string;
  date: Date;
  amount: number;
  amount_usd?: number;
  amount_eur?: number;
  description: string;
  person: string;
  image_url?: string; // Optional image URL
  hasInvoice?: boolean;
  creatorName?: string;
  personName?: string;
  imageLoadError?: boolean; // Add this new field to track image load errors
}

interface SitePhotosTabProps {
  projectId: string;
  projectStatus?: "active" | "on-track" | "delayed" | "completed";
}

// Helper function to map database entries to the Entry type
const mapDatabaseEntryToEntryType = async (dbEntry: any): Promise<Entry> => {
  // Fetch creator name if user_id exists
  let creatorName = "";
  if (dbEntry.user_id) {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('name')
        .eq('id', dbEntry.user_id)
        .single();
      
      if (!error && data) {
        creatorName = data.name || "";
      }
    } catch (err) {
      
    }
  }

  return {
    id: dbEntry.id,
    type: "site_photo", // Use key instead of hardcoded text
    date: new Date(dbEntry.date),
    amount: 0,
    description: dbEntry.description || "",
    person: "",
    image_url: dbEntry.file_url || dbEntry.image_url,
    hasInvoice: false,
    creatorName: creatorName,
    personName: "",
    imageLoadError: false
  };
};

const SitePhotosTab: React.FC<SitePhotosTabProps> = ({ projectId, projectStatus = "active" }) => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState("");
  const [viewingImage, setViewingImage] = useState<{ url: string; title: string } | null>(null);
  // States for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState<Entry | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { user } = useAuth();
  const [entries, setEntries] = useState<Entry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [failedImages, setFailedImages] = useState<Record<string, boolean>>({});
  
  // Get role-based permissions
  const permissions = useProjectRole(projectId);

  // Tier gate: free users can view existing photos but cannot upload new ones.
  const { status: subscriptionStatus } = useSubscription();
  const tierBlocked = !canUploadImages(subscriptionStatus);

  const isProjectCompleted = projectStatus === "completed";

  // Function to fetch entries
  const fetchEntries = async () => {
    if (!projectId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Call the API to get entries for this project
      const data = await entriesAPI.getEntriesByProject(projectId);
      
      // Filter to only include site photos
      const sitePhotos = data.filter(entry => entry.type === 'sitePhoto');
      
      // Map the data to our Entry type - using Promise.all to handle async mapping
      const mappedEntriesPromises = sitePhotos.map(mapDatabaseEntryToEntryType);
      const mappedEntries = await Promise.all(mappedEntriesPromises);
      
      setEntries(mappedEntries);
    } catch (err) {
      
      setError(t("site_photos.photos_loading_error"));
      toast.error(t("site_photos.photos_loading_error"));
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch entries data when component mounts or projectId changes
  useEffect(() => {
    fetchEntries();
  }, [projectId]);

  // Refresh entries when navigating back to this screen
  useEffect(() => {
    // This will run when component is focused (navigated back to)
    const handleFocus = () => {
      fetchEntries();
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  // Also refresh when path changes (navigating back from entry detail)
  useEffect(() => {
    fetchEntries();
  }, [location.pathname]);


  // Function to handle viewing an image
  const handleViewImage = (url: string, title: string) => {
    setViewingImage({ url, title });
  };

  // Function to handle delete entry
  const handleDeleteClick = (entry: Entry) => {
    setEntryToDelete(entry);
    setDeleteDialogOpen(true);
  };
  
  // Function to confirm deletion
  const confirmDelete = async () => {
    if (!entryToDelete) return;
    
    setIsDeleting(true);
    try {
      // Call the API to delete the entry
      await entriesAPI.deleteEntry(entryToDelete.id);
      
      // Update the local state after successful API call
      setEntries(prev => prev.filter(e => e.id !== entryToDelete.id));
      
      // Show success toast
      toast.success(t("site_photos.photo_deleted_successfully"));

      // Refresh entries after deletion to ensure everything is up to date
      fetchEntries();
    } catch (error) {
      
      toast.error(t("site_photos.photo_delete_error"));
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setEntryToDelete(null);
    }
  };

  // Function to handle clicking on an entry (for edit)
  const handleEntryClick = (entry: Entry) => {
    navigate(`/entry/${entry.id}`);
  };

  // Function to handle image load error
  const handleImageError = (entryId: string, imageUrl: string) => {
    if (!failedImages[imageUrl]) {
      // Update the failed images record to prevent retries
      setFailedImages(prev => ({
        ...prev,
        [imageUrl]: true
      }));
      
      // Log error once (not repeatedly)
      
    }
  };

  const filteredEntries = entries.filter((entry) => {
    return entry.description
      .toLowerCase()
      .includes(filter.toLowerCase());
  });

  // Render entry card
  const renderEntryCard = (entry: Entry) => {
    const imageHasError = failedImages[entry.image_url || ''];

    return (
      <div
        className="bg-white border border-gray-200 rounded-2xl shadow-sm p-4 cursor-pointer hover:shadow-md transition-shadow"
        onClick={() => handleEntryClick(entry)}
      >
        {/* Top row */}
        <div className="flex items-center justify-between mb-2">
          <MonoValue
            value={format(entry.date, "dd.MM.yyyy", { locale: tr })}
            tone="muted"
            size="meta"
          />
          <div className="flex items-center gap-1.5">
            {entry.image_url && !imageHasError && (
              <button
                type="button"
                className="text-brand-600 hover:text-brand-700 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewImage(entry.image_url!, `${entry.description}`);
                }}
              >
                <Image className="h-3.5 w-3.5" />
              </button>
            )}
            <StatusBadge tone="brand">{t("entries.types.site_photo")}</StatusBadge>
          </div>
        </div>

        <p className="text-[13px] font-medium text-gray-900 break-words">{entry.description}</p>

        {entry.creatorName && (
          <p className="text-[11px] text-gray-500 mt-1">
            <span className="font-semibold">{t("site_photos.uploaded_by")}: </span>
            {entry.creatorName}
          </p>
        )}

        {entry.image_url && (
          <div className="mt-3 rounded-xl overflow-hidden border border-gray-100">
            {imageHasError ? (
              <div className="w-full h-44 flex flex-col items-center justify-center bg-gray-50">
                <ImageOff className="h-8 w-8 mb-2 text-gray-400" />
                <span className="text-xs text-gray-500">{t("site_photos.image_load_failed")}</span>
              </div>
            ) : (
              <SecureImage
                src={entry.image_url}
                alt={entry.description}
                className="w-full h-44 object-cover"
                onClick={(e) => {
                  e.stopPropagation();
                  handleViewImage(entry.image_url!, `${entry.description}`);
                }}
                onError={() => handleImageError(entry.id, entry.image_url!)}
              />
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="pt-2">
      <div className="flex flex-col space-y-2 md:flex-row md:space-y-0 md:space-x-3 mb-4">
        <div className="flex-1">
          <Input
            placeholder={t("common.search")}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="w-full border-gray-200 rounded-lg bg-white"
          />
        </div>
        <Button
          className="bg-brand-600 hover:bg-brand-700 px-4"
          onClick={() => tierBlocked ? navigate('/subscription') : navigate("/new-entry")}
          disabled={isProjectCompleted || !permissions.canUploadFiles}
          title={
            tierBlocked
              ? t('photo_upload.premium_required', 'Fotoğraf yüklemek için Premium gerekli')
              : isProjectCompleted
                ? t("site_photos.completed_project_warning")
                : !permissions.canUploadFiles
                  ? t("site_photos.upload_permission_denied")
                  : ""
          }
        >
          {t("site_photos.new_photo")}
        </Button>
      </div>

      {tierBlocked && (
        <div className="mb-4 bg-amber-50 border border-amber-300 text-amber-900 rounded-xl px-4 py-2 text-sm flex items-center justify-between">
          <span>{t('photo_upload.premium_required', 'Fotoğraf yüklemek için Premium gerekli')}</span>
          <Button size="sm" variant="outline" onClick={() => navigate('/subscription')}>
            {t('subscription.upgrade', 'Yükselt')}
          </Button>
        </div>
      )}

      {isProjectCompleted && (
        <div className="mb-4 bg-warning-50 border border-warning-300 text-warning-600 rounded-xl px-4 py-2 text-sm">
          {t("site_photos.completed_project_warning")}
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-brand-600" />
            <p className="text-gray-500">{t("site_photos.loading_photos")}</p>
          </div>
        </div>
      ) : error ? (
        <div className="text-center py-8 text-error-600">
          <p>{error}</p>
          <Button
            variant="outline"
            size="sm"
            className="mt-4"
            onClick={() => window.location.reload()}
          >
            {t("common.retry")}
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filteredEntries.length > 0 ? (
            filteredEntries.map((entry) => (
              <div className="relative w-full" key={entry.id}>
                {renderEntryCard(entry)}
                {permissions.canUploadFiles && (
                  <button
                    className="absolute top-3 right-3 text-gray-400 hover:text-error-600 transition-colors z-20"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteClick(entry);
                    }}
                    aria-label="Sil"
                  >
                    <Trash className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))
          ) : (
            <div className="text-center py-12 text-gray-500 md:col-span-2">
              {t("site_photos.no_photos_found")}
            </div>
          )}
        </div>
      )}

      {/* Image Viewing Dialog */}
      {viewingImage && (
        <ImageViewDialog
          imageUrl={viewingImage.url}
          title={viewingImage.title}
          open={!!viewingImage}
          onOpenChange={(open) => {
            if (!open) setViewingImage(null);
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        title={t("site_photos.delete_photo")}
        description={t("site_photos.delete_photo_confirmation")}
        onConfirm={confirmDelete}
        isDeleting={isDeleting}
      />
    </div>
  );
};

export default SitePhotosTab;
